/**
 * 底部菜单（TabBar）模板共用逻辑
 *
 * 供 index_50000 / index_53000 / index_world_cup 等模板复用，
 * 各模板仅负责 UI 结构与样式差异，导航与选中态逻辑统一在此维护。
 */
import { rootRouteList } from '@/config/routes'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores'

/**
 * 底部菜单核心 composable
 *
 * @returns {Object} active - 当前选中 tab 索引（0~4）
 * @returns {Object} LoginRegisterRef - 登录注册弹窗 ref，未登录时拦截跳转
 * @returns {Function} onChange - van-tabbar @change 回调，处理路由跳转
 * @returns {ComputedRef<boolean>} show - 是否在根路由页展示底栏
 */
export function useTabBarTemplate() {
  const router = useRouter()
  const userStore = useUserStore()

  /** 是否已登录，除首页外其余 tab 点击均需登录 */
  const hadToken = computed(() => {
    return !!userStore.userInfo.token
  })

  /** van-tabbar v-model 绑定的当前索引 */
  const active = ref(0)
  const LoginRegisterRef = ref('')

  /**
   * 根路由名称 → tab 索引映射
   * 顺序与 van-tabbar-item 排列一致：Home / Deposito / Promocao / Activity / Profile
   */
  const routeToIndex = {
    Home: 0,
    Deposito: 1,
    Promocao: 2,
    Activity: 3,
    Profile: 4,
  }

  const route = useRoute()

  /** 根据当前路由同步 active，并持久化到 localStorage */
  const setActiveTab = () => {
    if (route.name && Object.prototype.hasOwnProperty.call(routeToIndex, route.name)) {
      active.value = routeToIndex[route.name]
      localStorage.setItem('active', active.value.toString())
    }
  }

  /** 路由变化时实时同步底栏高亮（含浏览器前进/后退） */
  watch(
    () => route.name,
    () => {
      setActiveTab()
    },
    { immediate: true }
  )

  /**
   * 初始化 active 优先级：
   * 1. localStorage 中有效的缓存索引
   * 2. 已登录则按当前路由推断
   * 3. 未登录默认首页（0）
   */
  onMounted(() => {
    const savedActive = localStorage.getItem('active')
    if (savedActive !== null) {
      const parsedActive = parseInt(savedActive, 10)
      if (!isNaN(parsedActive) && parsedActive >= 0 && parsedActive <= 4) {
        active.value = parsedActive
      }
    } else if (hadToken.value) {
      setActiveTab()
    } else {
      active.value = 0
    }
  })

  /**
   * tab 点击切换处理
   * @param {number} index - van-tabbar 回调索引，与 routeToIndex 对应
   *
   * 未登录：强制回首页并弹出登录框
   * index 1~3：push 跳转，Promocao / Activity 会读取 localStorage 中的 type 恢复上次子页
   * index 4（Profile）：使用 replace + sessionStorage，避免返回栈堆积
   * index 0（Home）：replace 回首页
   */
  const onChange = (index) => {
    if (!hadToken.value) {
      active.value = 0
      localStorage.setItem('active', '0')
      nextTick(() => {
        LoginRegisterRef.value?.openPopupRegLogin()
      })
      return
    }
    if (index == 1) {
      active.value = 1
      localStorage.setItem('active', active.value.toString())
      router.push({ name: 'Deposito' })
      return
    } else if (index == 2) {
      active.value = 2
      localStorage.setItem('active', active.value.toString())
      const PromocaoType = localStorage.getItem('PromocaoType')
      if (PromocaoType && PromocaoType !== undefined) {
        router.push({
          name: 'Promocao',
          query: {
            id: 123456,
            type: +PromocaoType,
          },
        })
      } else {
        router.push({ name: 'Promocao' })
      }
      return
    } else if (index == 3) {
      active.value = 3
      const ActivityType = localStorage.getItem('ActivityType')
      if (ActivityType && ActivityType !== undefined) {
        router.push({
          name: 'Activity',
          query: {
            id: 123456,
            type: +ActivityType,
          },
        })
      } else {
        router.push({ name: 'Activity' })
      }
      return
    } else if (index == 4) {
      active.value = 4
      sessionStorage.setItem('active', active.value.toString())
      router.replace({ name: 'Profile' })
      return
    } else {
      active.value = 0
      localStorage.setItem('active', '0')
      router.replace({ name: 'Home' })
    }
  }

  /** 仅在 rootRouteList 配置的根页面展示底栏，子页面自动隐藏 */
  const show = computed(() => {
    if (route.name && rootRouteList.includes(route.name)) {
      return true
    }
    return false
  })

  return {
    active,
    LoginRegisterRef,
    onChange,
    show,
  }
}

/**
 * 选中态图标主题色 mask 样式（index_50000 / index_53000 使用）
 *
 * 通过 CSS mask 将 theme 色渐变叠到 shadow 图标上，实现随皮肤变色的选中效果。
 *
 * @param {string} tab - 图标 key：home | top-up | activity | user
 * @param {string} template - 图包目录名，默认 index_50000，53000 模板传 index_53000
 * @returns {{ WebkitMaskImage: string, maskImage: string }}
 */
export function getTabOnThemeMaskStyle(tab, template = 'index_50000') {
  const url = getImageUrlWithCDN_LOCAL(`/tabbar/${template}/${tab}_on_theme.png`)
  const mask = `url("${url}")`
  return {
    WebkitMaskImage: mask,
    maskImage: mask,
  }
}

/**
 * 世界杯主题底栏专用图标（index_world_cup 使用）
 *
 * 激活态与中间大按钮均复用 index_50000 图包中的世界杯素材，
 * 未选中 off 图标仍引用 index_50000 目录下的通用资源。
 */
export function useTabBarWorldCupIcons() {
  return {
    tabbarActiveIcon: getImageUrlWithCDN_LOCAL('/tabbar/index_50000/active_world_cup.png'),
    tabbarCenterIcon: getImageUrlWithCDN_LOCAL('/tabbar/index_50000/acting_world_cup.png'),
  }
}
